import { z } from "zod";
import { newPasswordSchema } from "./auth";
import { pageQuerySchema } from "./pagination";
import {
  type Grant,
  PERMISSIONS,
  type PermissionId,
  type PermissionScope,
  permissionDef,
  STAFF_SCOPES,
  STAFF_SYSTEM_ROLES,
  withRequirements,
} from "./permissions";
import {
  ACCOUNT_STATUSES,
  type AccountStatus,
  blankable,
  emailSchema,
  optionalText,
  recordIdSchema,
  requiredText,
} from "./platform";
import { indianMobileSchema } from "./students";

/**
 * School settings → Staff and roles: a school's own staff accounts and the roles it makes.
 * Built-in roles are named by key ("teacher"); a school's own roles by id ("rol_…").
 */

// ---------------------------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------------------------

export const ROLE_STATUSES = ["active", "retired"] as const;
export type RoleStatus = (typeof ROLE_STATUSES)[number];

export const customRoleIdSchema = recordIdSchema("rol", "Choose a role");

/** A role someone can be given: a built-in staff role or one the school made. */
export const staffRoleIdSchema = z.union([z.enum(STAFF_SYSTEM_ROLES), customRoleIdSchema], {
  error: "Choose a role",
});

const PERMISSION_IDS = PERMISSIONS.map((entry) => entry.id) as [PermissionId, ...PermissionId[]];

export const grantSchema = z
  .object({
    permission: z.enum(PERMISSION_IDS, { error: "Unknown permission" }),
    scope: z.enum(STAFF_SCOPES, { error: "Choose who it covers" }),
  })
  .refine(
    (value) =>
      ((permissionDef(value.permission)?.scopes ?? []) as readonly PermissionScope[]).includes(
        value.scope,
      ),
    { error: "This permission can't be limited that way", path: ["scope"] },
  );

/** A role's permissions, each once, with whatever they need added (seeing before changing). */
export const roleGrantsSchema = z
  .array(grantSchema)
  .min(1, { error: "Choose at least one permission" })
  .max(PERMISSIONS.length)
  .refine((grants) => new Set(grants.map((grant) => grant.permission)).size === grants.length, {
    error: "Choose each permission once",
  })
  .transform((grants): Grant[] => withRequirements(grants));

const roleFields = {
  name: requiredText(60, "Enter a name for the role", "Name is too long"),
  description: optionalText(200, "Description is too long"),
  grants: roleGrantsSchema,
};

export const roleCreateSchema = z.object({
  ...roleFields,
  description: roleFields.description.default(null),
});

export const roleUpdateSchema = z.object({
  name: roleFields.name.optional(),
  description: roleFields.description.optional(),
  grants: roleFields.grants.optional(),
  status: z.enum(ROLE_STATUSES, { error: "Choose active or retired" }).optional(),
  version: z.number().int().min(1),
});

export type RoleCreate = z.output<typeof roleCreateSchema>;
export type RoleCreateInput = z.input<typeof roleCreateSchema>;
export type RoleUpdate = z.output<typeof roleUpdateSchema>;

export interface RoleDto {
  /** "teacher" for a built-in role, "rol_…" for the school's own. */
  id: string;
  kind: "built_in" | "custom";
  name: string;
  description: string | null;
  grants: Grant[];
  status: RoleStatus;
  /** Active accounts that hold it. */
  holders: number;
  /** Null for built-in roles, which can't be changed. */
  version: number | null;
}

// ---------------------------------------------------------------------------------------------
// Staff accounts
// ---------------------------------------------------------------------------------------------

const staffRoleIdsSchema = z
  .array(staffRoleIdSchema)
  .min(1, { error: "Choose at least one role" })
  .max(20, { error: "Choose fewer roles" })
  .transform((values) => [...new Set(values)].sort());

const staffFields = {
  displayName: requiredText(80, "Enter the person's name", "Name is too long"),
  designation: optionalText(80, "Designation is too long"),
  email: blankable(emailSchema),
  mobile: blankable(indianMobileSchema),
  roleIds: staffRoleIdsSchema,
};

/**
 * How a new person gets their first password: an emailed one-time link (they choose it), or a
 * temporary password the office hands over. Either way they set their own at first sign-in.
 */
export const staffAccessSchema = z.discriminatedUnion(
  "method",
  [
    z.object({ method: z.literal("link") }),
    z.object({ method: z.literal("password"), password: newPasswordSchema }),
  ],
  { error: "Choose how they get their password" },
);

export const staffCreateSchema = z
  .object({
    ...staffFields,
    designation: staffFields.designation.default(null),
    email: staffFields.email.default(null),
    mobile: staffFields.mobile.default(null),
    access: staffAccessSchema,
  })
  .refine((value) => value.access.method !== "link" || value.email !== null, {
    error: "Enter their email to send the link to",
    path: ["email"],
    when: (payload) => !payload.issues.some((issue) => issue.path?.[0] === "email"),
  });

export const staffUpdateSchema = z.object({
  displayName: staffFields.displayName.optional(),
  designation: staffFields.designation.optional(),
  email: staffFields.email.optional(),
  mobile: staffFields.mobile.optional(),
  roleIds: staffFields.roleIds.optional(),
  status: z.enum(ACCOUNT_STATUSES, { error: "Choose on or off" }).optional(),
  version: z.number().int().min(1),
});

export const staffListQuerySchema = pageQuerySchema.extend({
  q: z.string().trim().max(80).optional(),
  roleId: staffRoleIdSchema.optional(),
  status: z.enum(ACCOUNT_STATUSES).optional(),
});

export type StaffCreate = z.output<typeof staffCreateSchema>;
export type StaffCreateInput = z.input<typeof staffCreateSchema>;
export type StaffUpdate = z.output<typeof staffUpdateSchema>;
export type StaffListQuery = z.output<typeof staffListQuerySchema>;

export interface StaffDto {
  id: string;
  loginId: string;
  displayName: string;
  designation: string | null;
  email: string | null;
  /** Mobile numbers are stored encrypted; lists and forms show only the last four digits. */
  mobileLast4: string | null;
  roles: { id: string; name: string }[];
  /** Section labels ("8-A") they are class teacher of; set in School settings → Classes. */
  classTeacherOf: string[];
  status: AccountStatus;
  lastLoginAt: string | null;
  mustChangePassword: boolean;
  version: number;
  createdAt: string;
}

/** A new account, and whether the set-password email went out (null: a password was set). */
export interface StaffCreatedDto {
  staff: StaffDto;
  setupEmailSent: boolean | null;
}
